import { format, parseISO } from 'date-fns'
import { navigate } from 'gatsby'
import {
  Avatar,
  BrandColors,
  Caption,
  Column,
  Divider,
  Label,
  MediaQueries,
  Opacities,
  Opacity,
  Padding,
  rem,
  Row,
  SizedBox,
  SystemColors,
  TextButton,
} from '@collabhouse/shared'
import React, { type FunctionComponent, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import Grid, { GridItem } from '../../components/grid/Grid'
import { useGroup } from '../../hooks/useGroup'
import { type Collaboration, CollaborationNextAction, type Term } from '../../providers/CollaborationProvider.types'
import {
  getCollaborationNextAction,
  getCollaborationNextActionInfo,
  getCollaborationNextActionStyles,
  getCollabStatus,
} from '../../utils/collaboration'
import { getImageSize, ImageSize } from '../../utils/imageSize'
import { getRolesInCollaboration, type Roles } from '../../utils/roles'
import { getUrlWithParams, routes } from '../../utils/routes'
import { getFormattedArtists } from '../../utils/track'
import CollaborationPlatform from '../collaboration-platform/CollaborationPlatform'
import CollaborationPrice from '../collaboration-price/CollaborationPrice'
import MobileOnly from '../mobile-only/MobileOnly'

const navigateToPublicGroupUrl = (groupId: string): void =>
  void navigate(
    getUrlWithParams(routes.collaboration.publicGroup.url, {
      groupId,
    }),
  )

export interface CollaborationCardProps {
  collaboration: Collaboration
  detailsButtonLabel?: string
  onClick: () => void
  small?: boolean
}

const CollaborationCard: FunctionComponent<React.PropsWithChildren<CollaborationCardProps>> = ({
  collaboration,
  onClick,
  small = false,
  detailsButtonLabel = 'View details',
}) => {
  const { group: myGroup } = useGroup()
  const [roles, setRoles] = useState<Roles>(null)
  const [collaborationTerms, setCollaborationTerms] = useState<Term | null>(null)
  const myGroupIsTheArtist = collaboration.from.id === myGroup.id

  const nextAction = useMemo(() => {
    if (collaboration && myGroup) {
      return getCollaborationNextAction(collaboration, myGroup?.id) || CollaborationNextAction.none
    }

    return CollaborationNextAction.none
  }, [collaboration, myGroup])

  useEffect(() => {
    const recentTerms = collaboration?.terms?.sort((first, second) => second.version - first.version)[0]

    setCollaborationTerms(recentTerms)
  }, [collaboration])

  useEffect(() => {
    setRoles(
      getRolesInCollaboration(collaboration?.from?.id, collaboration?.to?.id, collaborationTerms?.createdByGroup?.id),
    )
  }, [collaboration, collaborationTerms, myGroup])

  const approvalState = nextAction === CollaborationNextAction.reviewCollab

  const collaborationNextInfo = getCollaborationNextActionInfo(
    nextAction,
    collaboration.id,
    nextAction === CollaborationNextAction.provideFeedback
      ? collaborationTerms?.posts[0].proofOfDeliveries[0].id
      : collaborationTerms?.posts[0].id,
  )

  return (
    <StyledCollaborationCard approvalState={approvalState}>
      <Padding
        vertical={{
          desktop: small ? rem(16) : rem(32),
          mobile: small ? rem(16) : rem(24),
        }}
        horizontal={{ desktop: small ? rem(16) : rem(32), mobile: rem(16) }}
      >
        <Row>
          {collaborationTerms && (
            <CardGrid>
              {roles && myGroupIsTheArtist && myGroupIsTheArtist ? (
                <SelfAlignedGridItem desktop={small ? 6 : 3} mobile={12}>
                  <Row crossAxisAlignment="center">
                    <Column>
                      <Avatar
                        size={{ desktop: rem(48), mobile: rem(40) }}
                        src={getImageSize(collaboration?.to?.avatar, ImageSize.small)?.url}
                      />
                    </Column>
                    <SizedBox width={{ desktop: rem(16), mobile: rem(8) }} />
                    <Column style={{ maxWidth: '100%', overflow: 'hidden' }}>
                      <>
                        <Row>
                          <WrappedTextButton
                            size="small"
                            variant="secondary"
                            alignment="left"
                            onClick={() => {
                              navigateToPublicGroupUrl(collaboration.to.id)
                            }}
                          >
                            {collaboration.to?.name}
                          </WrappedTextButton>
                        </Row>
                        {!small && (
                          <Opacity opacity={Opacities.fiftyfive}>
                            <Caption size="small">Content Creator</Caption>
                          </Opacity>
                        )}
                      </>
                    </Column>
                  </Row>
                </SelfAlignedGridItem>
              ) : (
                <SelfAlignedGridItem desktop={small ? 6 : 3} mobile={12}>
                  <Row crossAxisAlignment="center">
                    <Column>
                      <Avatar
                        size={{ desktop: rem(48), mobile: rem(40) }}
                        src={getImageSize(collaboration?.from?.avatar, ImageSize.small)?.url}
                      />
                    </Column>
                    <SizedBox width={{ desktop: rem(16), mobile: rem(8) }} />
                    <Column style={{ minWidth: 0 }}>
                      <>
                        <Row>
                          <WrappedTextButton
                            size="small"
                            variant="secondary"
                            alignment="left"
                            onClick={() => {
                              navigateToPublicGroupUrl(collaboration.from.id)
                            }}
                          >
                            {collaboration.from?.name}
                          </WrappedTextButton>
                        </Row>
                        {!small && (
                          <Opacity opacity={Opacities.fiftyfive}>
                            <Caption size="small">Music Maker</Caption>
                          </Opacity>
                        )}
                      </>
                    </Column>
                  </Row>
                </SelfAlignedGridItem>
              )}

              {!small && (
                <SelfAlignedGridItem desktop={3} mobile={12}>
                  <Row crossAxisAlignment="center">
                    <Column>
                      {collaborationTerms.track && (
                        <Avatar
                          shape="square"
                          size={{ desktop: rem(48), mobile: rem(40) }}
                          src={getImageSize(collaborationTerms.track?.product?.artwork, ImageSize.small)?.url}
                        />
                      )}
                    </Column>
                    <SizedBox width={{ desktop: rem(16), mobile: rem(8) }} />
                    <TrackColumn>
                      {collaborationTerms.track && (
                        <TextOverflowEllipsis size="small" noWrap>
                          {collaborationTerms.track.name}
                        </TextOverflowEllipsis>
                      )}
                      <Opacity opacity={Opacities.fiftyfive}>
                        <Caption size="small" noWrap>
                          {getFormattedArtists(collaborationTerms.track.trackArtists)}
                        </Caption>
                      </Opacity>
                    </TrackColumn>
                  </Row>

                  <MobileOnly>
                    <Opacity opacity={Opacities.twenty}>
                      <Padding top={rem(16)}>
                        <Divider />
                      </Padding>
                    </Opacity>
                  </MobileOnly>
                </SelfAlignedGridItem>
              )}

              <SelfAlignedGridItem desktop={3} mobile={6}>
                <CollaborationPrice collaborationTerms={collaborationTerms} noIcon={small} />
              </SelfAlignedGridItem>

              {collaborationTerms.posts && (
                <GridItem desktop={3} mobile={6}>
                  <CollaborationPlatform
                    collaborationTerms={collaborationTerms}
                    collaborationPosts={collaborationTerms.posts}
                    small={small}
                  />
                </GridItem>
              )}
            </CardGrid>
          )}
        </Row>

        <SizedBox height={{ desktop: rem(20), mobile: rem(16) }} />

        <Opacity opacity={Opacities.twenty}>
          <Divider />
        </Opacity>

        <SizedBox height={{ desktop: rem(20), mobile: rem(16) }} />

        <Row>
          <CardGrid>
            <GridItem desktop={3} mobile={6}>
              <Row crossAxisAlignment="center">
                <Column>
                  <Opacity opacity={Opacities.fiftyfive}>
                    <Caption size="small">Status:</Caption>
                  </Opacity>
                  <Caption size="small">{getCollabStatus(collaboration.state)}</Caption>
                </Column>
              </Row>
            </GridItem>

            <GridItem desktop={3} mobile={6}>
              <Row crossAxisAlignment="center">
                <Column>
                  <Opacity opacity={Opacities.fiftyfive}>
                    <Caption size="small">Action required:</Caption>
                  </Opacity>
                  {nextAction && (
                    <Opacity opacity={getCollaborationNextActionStyles(nextAction).opacity}>
                      <NextActionCaption color={getCollaborationNextActionStyles(nextAction).color} size="small">
                        {collaborationNextInfo.link ? (
                          <TextButton
                            size="small"
                            variant="secondary"
                            onClick={(event) => {
                              event.preventDefault()
                              void navigate(collaborationNextInfo.link)
                            }}
                          >
                            {collaborationNextInfo.status}
                          </TextButton>
                        ) : (
                          collaborationNextInfo.status
                        )}
                      </NextActionCaption>
                    </Opacity>
                  )}
                </Column>
              </Row>
            </GridItem>

            <GridItem desktop={3} mobile={6}>
              <Row crossAxisAlignment="center">
                <Column>
                  <Opacity opacity={Opacities.fiftyfive}>
                    <Caption size="small">Last updated:</Caption>
                  </Opacity>
                  <Caption size="small">
                    {collaboration.createdAt && format(parseISO(collaboration.createdAt), 'dd-MM-yyyy')}
                  </Caption>
                </Column>
              </Row>
            </GridItem>

            <GridItem desktop={3} mobile={6}>
              <Row mainAxisAlignment="flex-start" crossAxisAlignment="flex-end">
                <>
                  <TextButton
                    size="small"
                    onClick={(event) => {
                      event.preventDefault()
                      onClick()
                    }}
                  >
                    {detailsButtonLabel}
                  </TextButton>
                  <SizedBox height={rem(40)} />
                </>
              </Row>
            </GridItem>
          </CardGrid>
        </Row>
      </Padding>
    </StyledCollaborationCard>
  )
}

export default CollaborationCard

const StyledCollaborationCard = styled(Column)<{ approvalState?: boolean }>`
  background: ${BrandColors.haiti.toString()};
  ${({ approvalState }) => approvalState && `border-left: ${rem(4)} solid ${SystemColors.info.toString()}`}
`

const SelfAlignedGridItem = styled(GridItem)`
  align-self: center;
`

const CardGrid = styled(Grid)`
  column-gap: ${rem(16)};
  row-gap: ${rem(16)};

  ${MediaQueries.desktop} {
    column-gap: ${rem(32)};
    row-gap: ${rem(20)};
  }
`

const WrappedTextButton = styled(TextButton)`
  display: inline-block;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`

const TextOverflowEllipsis = styled(Label)`
  width: ${rem(240)};
  display: inline-block;

  ${MediaQueries.desktop} {
    width: ${rem(200)};
  }
`

const TrackColumn = styled(Column)`
  min-width: 0;
`

interface NextActionCaptionProps {
  color: string
}

const NextActionCaption = styled(Caption)<NextActionCaptionProps>`
  color: ${({ color }) => color};
`
